/**
 * MainProjectPicker.jsx - the record editor's "Main project" (10/14/26):
 * which record this one sits under (projects.parent_id), set by hand - the
 * only way records are grouped ("None" = on its own). One searchable
 * dropdown over the records by project number, name, PO # or customer
 * (the same customer's first), never this record or one under it. Saved
 * with the editor's Save. Module scope, never defined in a render body.
 *
 * Opened with nothing typed it lists the same customer's records; with no
 * customer on the form (or none of theirs) the most recent records. A blank
 * form customer must not match blank-customer records, or the list shows
 * only the odd record with no customer.
 */
import { useMemo } from 'react';
import { labelSt, rowSt } from '../../opportunities/editOpportunityConstants';
import { descendantIds, recordLabel, recordSearchText } from '../../../data/projectHierarchy';
import SearchSelect from '../../ui/SearchSelect';

const RECENT_SHOWN = 50;
export const MAIN_PROJECT_HINT = 'Type to search any record by project #, name, PO # or customer';

const norm = (s) =>
  String(s || '')
    .trim()
    .toLowerCase();
const createdOf = (r) => String(r.createdAt || r.created_at || r.quoteDate || r.quote_date || '');

/** The picker's lists: `options` (every pickable record, the customer's first) and `browse` (shown with nothing typed). */
export function mainProjectOptions(records = [], selfId = null, customer = '') {
  const below = selfId ? descendantIds(records, selfId) : new Set();
  const cust = norm(customer);
  const rows = records
    .filter((r) => String(r.id) !== String(selfId) && !below.has(String(r.id)))
    .map((r) => ({
      value: String(r.id),
      label: `${recordLabel(r)}${r.customer ? ` (${r.customer})` : ''}`,
      text: recordSearchText(r),
      mine: !!cust && norm(r.customer) === cust,
      created: createdOf(r),
    }));
  const options = [...rows].sort((a, b) => b.mine - a.mine || a.label.localeCompare(b.label));
  const mine = options.filter((o) => o.mine);
  const browse = mine.length
    ? mine
    : [...rows].sort((a, b) => b.created.localeCompare(a.created) || b.label.localeCompare(a.label)).slice(0, RECENT_SHOWN);
  return { options, browse, hasMine: mine.length > 0 };
}

/** Props: records (every record), selfId, customer (the form's), value (parent id or ''), onChange(id). */
export default function MainProjectPicker({ records = [], selfId = null, customer = '', value = '', onChange }) {
  const { options, browse, hasMine } = useMemo(() => mainProjectOptions(records, selfId, customer), [records, selfId, customer]);
  return (
    <div style={rowSt} data-field="parentId">
      <div style={labelSt}>Main project</div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <SearchSelect
          options={options}
          browse={browse}
          hint={`${MAIN_PROJECT_HINT}${hasMine ? " - the same customer's records first." : ' - the most recent below.'}`}
          value={value || ''}
          onChange={onChange}
          defaultLabel="None (not under another project)"
          valueLabel={value ? `${value} (not in the list)` : ''}
          label="Main project"
          title="The record this one sits under"
          style={{ width: '100%' }}
        />
        <div style={{ fontSize: 10.5, color: '#777', marginTop: 3, lineHeight: 1.35 }}>
          {value ? 'This record sits under the one picked.' : 'On its own.'} Open the list and type a project #, name, PO # or customer to
          find any record.
        </div>
      </div>
    </div>
  );
}
